import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PredictionFrequencyBarChart from './PredictionFrequencyBarChart';
import { formatNumberWithComma } from '../../../utils/formatText';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../utils/formatting';
import { theme } from '../../../styles/theme';
import ThinDivider from '../../ThinDivider';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import KubitTooltip from '../../KubitTooltip';
import { SvgIcon } from '../../Icon';

const Container = styled(({ height, ...props }) => <div {...props} />)`
  height: ${({ height }) => height};
  padding: 20px 16px;
  width: 100%;
  overflow: auto;
  ${floatingComponentStyles};
`;

const EventHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #8a94bd;
  display: inline-block;
  margin-bottom: 20px;
`;

const HeaderAssistMessage = styled.span`
  font-weight: 500;
  color: #c4c9de;
`;

const SummaryRow = styled.div`
  display: flex;
`;
const SummaryItem = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  width: 33.33%;
`;
const SummaryTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 4px;
`;
const SummaryDescription = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8a94bd;
  display: inline-block;
`;

const StyledSvgIcon = styled(SvgIcon)`
  margin-left: 4px;
  height: 14px;
  width: 14px;
  vertical-align: middle;
`;

const HighlightedDescription = styled.span`
  color: ${theme.colors.mainBlue};
`;

const Splitter = styled(ThinDivider)`
  margin-top: 16px;
  margin-bottom: 16px;
`;

const TableTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #8a94bd;
`;

const DataTable = styled.table`
  margin-top: 8px;
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  border: 1px solid ${theme.colors.border};
  font-size: 12px;
  font-weight: 600;
  color: #8a94bd;
  padding: 8px;
`;

const TableRow = styled(({ whiteBackground, ...props }) => <tr {...props} />)`
  height: 32px;
  background-color: ${({ whiteBackground }) =>
    whiteBackground ? '#fff' : '#f7f8fe'};
`;

const TableCell = styled(({ fontWeight = 500, ...props }) => <td {...props} />)`
  font-size: 12px;
  font-weight: ${({ fontWeight }) => fontWeight};
  color: #8a94bd;
  border: 1px solid ${theme.colors.border};
  padding: 8px;
  text-align: center;
`;
export default ({ data, eventName, analysisId, cellId }: any) => {
  const statisticFields = [
    {
      key: 'correlation',
      display: 'Correlation',
      description: [
        'Pearson correlation is the geometric mean of how',
        'predictive X is of Y and how predictive Y is of X. In',
        'this case, X is performing at least the threshold',
        'number of events and Y is being retained.',
      ],
    },
    {
      key: 'ppv',
      display: 'Positive Predictive Value',
      description: [
        'Proportion of users performing at least the threshold',
        'number of events who were also retained.',
      ],
    },
    {
      key: 'npv',
      display: 'Negative Predictive Value',
      description: [
        'Proportion of users performing less than the',
        'threshold number of events who were not retained.',
      ],
    },
    {
      key: 'sensitivity',
      display: 'Sensitivity',
      description: [
        'Proportion of retained users who performed at least',
        'the threshold number of events.',
      ],
    },
    {
      key: 'specificity',
      display: 'Specificity',
      description: [
        'Proportion of not retained users who performed less',
        'than the threshold number of events.',
      ],
    },
    {
      key: 'pat',
      display: 'Proportion Above Threshold',
      description: [
        'Proportion of users performing at least the',
        'threshold number of events.',
      ],
    },
  ];
  const [freqIndex, setFreqIndex] = useState(data.strongestFrequencyIndex);
  const [containerHeight, setContainerHeight] = useState('auto');
  // temp use stronegst
  const currentDataSet = data?.eventFrequencyCorrelations?.[freqIndex];

  const getPredictionLevel = (correlation: number) => {
    if (correlation < -0.4) {
      return 'High Negative';
    } else if (-0.4 <= correlation && correlation < -0.3) {
      return 'Moderate Negative';
    } else if (-0.3 <= correlation && correlation < -0.2) {
      return 'Slight Negative';
    } else if (-0.2 <= correlation && correlation < 0.2) {
      return 'Not Predictive';
    } else if (0.2 <= correlation && correlation < 0.3) {
      return 'Slight';
    } else if (0.3 <= correlation && correlation < 0.4) {
      return 'Moderate';
    } else if (0.4 <= correlation) {
      return 'High';
    }
  };

  const getPredictedUserCount = (totalUsers: number, percentage: number) => {
    return formatNumberWithComma(Math.floor(totalUsers * percentage));
  };

  useEffect(() => {
    const cellElement = document.getElementById(cellId);
    const innerHeight = window.innerHeight;
    if (cellElement) {
      const rect = cellElement.getBoundingClientRect();
      const topOffset = innerHeight - rect.top;
      if (topOffset < 950) {
        setContainerHeight('500px');
      } else {
        setContainerHeight('auto');
      }
    }
  }, [cellId]);

  return (
    <Container height={containerHeight}>
      <EventHeader>
        {eventName}
        <HeaderAssistMessage> (within </HeaderAssistMessage>
        {`${data.columnIndex + 1} ${data.columnIndex === 0 ? 'day' : 'days'}`}
        <HeaderAssistMessage>)</HeaderAssistMessage>
      </EventHeader>
      <SummaryRow>
        <SummaryItem>
          <SummaryTitle>
            {getPredictionLevel(currentDataSet?.correlation)}
          </SummaryTitle>
          <div style={{ display: 'flex' }}>
            <SummaryDescription>Predictive Level</SummaryDescription>
            <KubitTooltip
              content={
                <div>
                  <div>{'High: Correlation Value ≥ 0.4'}</div>
                  <div>{'Moderate: 0.4 > Correlation Value ≥ 0.3'}</div>
                  <div>{'Slight: 0.3 > Correlation Value ≥ 0.2'}</div>
                  <br />
                  <div>{'Not Predictive: -0.2 ≤ Correlation Value < 0.2'}</div>
                  <br />
                  <div>
                    {'Slight Negative: -0.3 ≤ Correlation Value < -0.2'}
                  </div>
                  <div>
                    {'Moderate Negative: -0.4 ≤ Correlation Value < -0.3'}
                  </div>
                  <div>{'High Negative: Correlation Value < -0.4'}</div>
                </div>
              }
              placement="bottom"
              noOpenDelay={true}
            >
              <StyledSvgIcon name="info" fontWeight="" />
            </KubitTooltip>
          </div>
        </SummaryItem>
        <SummaryItem>
          <SummaryTitle>
            {globalFormat.formatNumber(
              currentDataSet?.correlation,
              defaultNumberFormatOptions.originDecimalPoint,
            )}
          </SummaryTitle>
          <div style={{ display: 'flex' }}>
            <SummaryDescription>Correlation Value</SummaryDescription>
            <KubitTooltip
              content={
                <div>
                  <div>
                    {'Pearson correlation is the geometric mean of how'}
                  </div>
                  <div>
                    {'predictive X is of Y and how predictive Y is of X.'}
                  </div>
                  <div>
                    {'In this case, X is performing at least the threshold'}
                  </div>
                  <div>{'number of events and Y is being retained.'}</div>
                </div>
              }
              placement="bottom"
              noOpenDelay={true}
            >
              <StyledSvgIcon name="info" />
            </KubitTooltip>
          </div>
          <SummaryDescription style={{ fontWeight: 500 }}>
            (Margin of error:{' '}
            <HighlightedDescription>
              ±{' '}
              {globalFormat.formatNumber(
                currentDataSet?.marginOfError,
                defaultNumberFormatOptions.originDecimalPoint,
              )}
            </HighlightedDescription>
            )
          </SummaryDescription>
        </SummaryItem>
        <SummaryItem>
          <SummaryTitle>
            {globalFormat.formatNumber(
              currentDataSet?.percentage,
              defaultNumberFormatOptions.percent,
            )}
          </SummaryTitle>
          <SummaryDescription>Users who did this</SummaryDescription>
          <SummaryDescription style={{ fontWeight: 500 }}>
            {`(${getPredictedUserCount(
              currentDataSet?.totalUsers,
              currentDataSet?.percentage,
            )} of ${formatNumberWithComma(currentDataSet?.totalUsers)})`}
          </SummaryDescription>
        </SummaryItem>
      </SummaryRow>
      <Splitter layout="horizontal" size="full" />
      <PredictionFrequencyBarChart
        freqIndex={freqIndex}
        setFreqIndex={setFreqIndex}
        analysisId={analysisId}
        data={data?.predictionChartData}
      />
      <Splitter layout="horizontal" size="full" />
      <div style={{ display: 'flex' }}>
        <TableTitle>Correlation Table</TableTitle>
        <KubitTooltip
          content={
            <div>
              <div>{'This table displays the following raw user counts,'}</div>
              <div>
                {'starting clockwise from the upper left: True Positive,'}
              </div>
              <div>{'False Positive, True Negative, False Negative.'}</div>
            </div>
          }
          placement="bottom"
          noOpenDelay={true}
        >
          <StyledSvgIcon name="info" />
        </KubitTooltip>
      </div>
      <DataTable>
        <thead>
          <TableRow>
            <TableHeader style={{ textAlign: 'left' }}>
              Event Frequency
            </TableHeader>
            <TableHeader style={{ fontWeight: 500 }}>
              In Target Cohort
            </TableHeader>
            <TableHeader style={{ fontWeight: 500 }}>
              NOT in Target Cohort
            </TableHeader>
          </TableRow>
        </thead>
        <tbody>
          <TableRow whiteBackground>
            <TableCell fontWeight={600} style={{ textAlign: 'left' }}>
              &#8805; {`${freqIndex + 1} ${freqIndex > 0 ? ' times' : ' time'}`}
            </TableCell>
            <TableCell>
              <div>
                {globalFormat.formatNumber(
                  currentDataSet?.truePositive,
                  defaultNumberFormatOptions.integer,
                )}
              </div>
              <div style={{ fontSize: '10px' }}>(True Positive)</div>
            </TableCell>
            <TableCell>
              <div>
                {globalFormat.formatNumber(
                  currentDataSet?.falsePositive,
                  defaultNumberFormatOptions.integer,
                )}
              </div>
              <div style={{ fontSize: '10px' }}>(False Positive)</div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell fontWeight={600} style={{ textAlign: 'left' }}>
              {`< ${freqIndex + 1} ${freqIndex > 0 ? ' times' : ' time'}`}
            </TableCell>
            <TableCell>
              <div>
                {' '}
                {globalFormat.formatNumber(
                  currentDataSet?.trueNegative,
                  defaultNumberFormatOptions.integer,
                )}
              </div>
              <div style={{ fontSize: '10px' }}>(False Negative)</div>
            </TableCell>
            <TableCell>
              <div>
                {' '}
                {globalFormat.formatNumber(
                  currentDataSet?.falseNegative,
                  defaultNumberFormatOptions.integer,
                )}
              </div>
              <div style={{ fontSize: '10px' }}>(True Negative)</div>
            </TableCell>
          </TableRow>
        </tbody>
      </DataTable>
      <div style={{ display: 'flex', marginTop: '16px', marginBottom: '8px' }}>
        <TableTitle>Detailed Statistics</TableTitle>
        <KubitTooltip
          content={
            <div>
              <div>
                {'The detailed statistics are a series of ratios that can'}
              </div>
              <div>
                {'be helpful in interpreting the correlation score. They'}
              </div>
              <div>
                {
                  'can all be generated directly from the user counts in the correlation table.'
                }
              </div>
            </div>
          }
          placement="bottom"
          noOpenDelay={true}
        >
          <StyledSvgIcon name="info" />
        </KubitTooltip>
      </div>
      <DataTable>
        <tbody>
          {statisticFields.map((option: any, index: number) => {
            return (
              <TableRow key={option.key} whiteBackground={index % 2 !== 0}>
                <TableCell style={{ textAlign: 'left' }} fontWeight={600}>
                  {option.display}
                  <KubitTooltip
                    content={
                      <div>
                        {option.description.map((text: string) => (
                          <div key={text}>{text}</div>
                        ))}
                      </div>
                    }
                    placement="bottom"
                    noOpenDelay={true}
                  >
                    <StyledSvgIcon name="info" />
                  </KubitTooltip>
                </TableCell>
                <TableCell>
                  {globalFormat.formatNumber(currentDataSet[option.key], {
                    style: 'decimal',
                    decimalPlace: 4,
                  })}
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </DataTable>
    </Container>
  );
};
